/*
  I-1847 (`docs/concept-web-interaction-defects.md`, HV-9 to HV-11): hover in script runs only for a
  pointer that hovers. The CSS step (I-1647) gates `:hover` rules, but a handler bound to
  `pointerenter`, `pointerover`, `mouseenter` or a pointer move runs for every pointer: a finger that
  starts a scroll on an info mark fires `pointerenter` with no click after it, and the note opened under
  it. A pointer that hovers is a mouse (a trackpad included), or a pen held above the screen, which
  reports no button down; a pen pressed to the screen, like a finger, has already arrived.

  Read per event, not from `matchMedia('(any-hover: hover)')`: an iPad with a trackpad answers both, and
  only the event says which of them is moving.
*/

import type { PointerEvent as ReactPointerEvent } from 'react';

/** The part of a pointer event this reads; a DOM `PointerEvent` and React's both carry it. */
type PointerLike = Pick<PointerEvent, 'pointerType' | 'buttons'>;

/** True for a mouse, or a pen with no button down: a pointer that can be over something unpressed. */
export function isHoverPointer(event: PointerLike): boolean {
  return event.pointerType === 'mouse' || (event.pointerType === 'pen' && event.buttons === 0);
}

/** `handler`, run only when the event comes from a pointer that hovers (`isHoverPointer`). */
export function onHoverPointer<E extends Element>(
  handler: (event: ReactPointerEvent<E>) => void,
): (event: ReactPointerEvent<E>) => void {
  return (event) => {
    if (isHoverPointer(event)) handler(event);
  };
}
